import { useCallback, useEffect, useRef, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
} from "firebase/firestore";
import { auth, db } from "./FirebaseConfig";

const PAGE_SIZE = 30;

// Gallery一覧では解析結果(results / poreContour等)を保持しない。
// Firestore Web SDKはdocument projectionを行わないため通信時にはdocument全体が返るが、
// stateへ入れる前に軽量メタデータだけへ縮約し、ブラウザメモリを抑える。
const toListPost = (docSnap) => {
  const data = docSnap.data() || {};
  return {
    photopath: data.photopath || docSnap.id,
    cultiverName: data.cultiverName || "",
    freeText: data.freeText || "",
    updatedAt: data.updatedAt ?? null,
    timestamp: data.timestamp ?? null,
    userName: data.userName || auth.currentUser?.email || "",
    important: data.important || "",
    analysisStatus: data.analysisStatus || "",
    date: data.date || "",
  };
};

export const usePosts = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);

  const lastDocRef = useRef(null);
  const requestIdRef = useRef(0);

  const fetchFirstPage = useCallback(async (targetUser = auth.currentUser) => {
    const email = targetUser?.email;
    const requestId = ++requestIdRef.current;

    if (!email) {
      setPosts([]);
      setLoading(false);
      setHasMore(false);
      lastDocRef.current = null;
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const q = query(
        collection(db, email),
        orderBy("timestamp", "desc"),
        limit(PAGE_SIZE)
      );
      const snap = await getDocs(q);
      if (requestId !== requestIdRef.current) return;

      const nextPosts = snap.docs.map(toListPost);
      setPosts(nextPosts);
      lastDocRef.current = snap.docs[snap.docs.length - 1] || null;
      setHasMore(snap.docs.length === PAGE_SIZE);
    } catch (e) {
      if (requestId !== requestIdRef.current) return;
      console.error("usePosts first page failed (timestamp paging)", e);
      setError(e);
      setPosts([]);
      setHasMore(false);
      lastDocRef.current = null;
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, []);

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore) return;
    const email = auth.currentUser?.email || user?.email;
    const cursor = lastDocRef.current;
    if (!email || !cursor) {
      setHasMore(false);
      return;
    }

    setLoadingMore(true);
    setError(null);
    try {
      const q = query(
        collection(db, email),
        orderBy("timestamp", "desc"),
        startAfter(cursor),
        limit(PAGE_SIZE)
      );
      const snap = await getDocs(q);
      const nextPosts = snap.docs.map(toListPost);

      setPosts((prev) => {
        const seen = new Set(prev.map((p) => p.photopath));
        return [...prev, ...nextPosts.filter((p) => !seen.has(p.photopath))];
      });
      lastDocRef.current = snap.docs[snap.docs.length - 1] || cursor;
      setHasMore(snap.docs.length === PAGE_SIZE);
    } catch (e) {
      console.error("usePosts loadMore failed (timestamp paging)", e);
      setError(e);
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loading, loadingMore, user?.email]);

  const refetch = useCallback(async () => {
    lastDocRef.current = null;
    setHasMore(true);
    await fetchFirstPage(auth.currentUser || user);
  }, [fetchFirstPage, user]);

  // Firestore削除成功後、全件を再取得せず一覧から即時削除する。
  const removePost = useCallback((photopath) => {
    if (!photopath) return;
    setPosts((prev) => prev.filter((post) => post.photopath !== photopath));
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      requestIdRef.current += 1;
      setUser(currentUser);
      lastDocRef.current = null;
      setHasMore(true);

      if (!currentUser) {
        setPosts([]);
        setLoading(false);
        setLoadingMore(false);
        setHasMore(false);
        setError(null);
        return;
      }

      void fetchFirstPage(currentUser);
    });

    return () => {
      requestIdRef.current += 1;
      unsubscribe();
    };
  }, [fetchFirstPage]);

  return {
    posts,
    loading,
    loadingMore,
    hasMore,
    error,
    user,
    refetch,
    loadMore,
    removePost,
  };
};
